<template>
  <section v-if="isReady" ref="productCtaContainer" class="product-images-cta-container">
    <component
      :is="item.url ? 'a' : 'button'"
      v-for="(item, itemIndex) in sortedList"
      v-bind="item.url ? { href: item.url, target: item.allowOpeningInNewTab ? '_blank' : '_self' } : {}"
      :key="`cta_item_${item.id || itemIndex}`"
      :class="['cta-item', { 'with-arrow-icon': item.showArrow }, item.customCssClass || '']"
      :style="item.customStyle || null"
      :rel="item.isAR && 'ar'"
      :aria-label="item.label.split('<br>')?.[0].toLowerCase()"
      @click="handleCtaClick(item)"
      @mouseenter="handleCtaMouseEnter(item)"
      @mouseleave="handleCtaMouseLeave(item)"
      @focus="handleCtaMouseEnter(item)"
      @blur="handleCtaMouseLeave(item)"
    >
      <img v-if="item.isAR" src="" class="empty-image" alt="AR view icon" />
      <span :class="[{ 'cta-item--label': !item.customLabelClass }, item.customLabelClass || '']" v-html="item.label"></span>
      <span v-if="item.showArrow">
        <Icon :icon="csgChevronLargeDown" size="s" theme="light" class="cta-item--chevron" />
      </span>
    </component>
  </section>
</template>

<script setup>
import { ref } from 'vue';
import Icon from '@atomic-ui/icon/Icon';
import { csgChevronLargeDown } from '@csgSvgIcons/icons';
import useProductImagesCta from '~/composables/useProductImagesCta/useProductImagesCta';

const props = defineProps({
  productId: {
    type: Number, // uses product?.product?.id from xapi response, which is of type Number
    required: true,
  },
});

const productCtaContainer = ref(null);

const { sortedList, isReady } = useProductImagesCta(props.productId, productCtaContainer);

const handleCtaClick = (item) => {
  if (item.onClickHandler) {
    item.onClickHandler();
  }

  // move focus to outfitting section for accessibility
  if (item.id === 'outfitting') {
    const outfittingSection = document.querySelector('#stylitics-container');
    if (outfittingSection) {
      const firstInteractive = outfittingSection.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (firstInteractive) {
        firstInteractive.focus();
      }
    }
  }
};

const handleCtaMouseEnter = (item) => {
  if (item.onMouseEnterHandler) {
    item.onMouseEnterHandler();
  }
};

const handleCtaMouseLeave = (item) => {
  if (item.onMouseLeaveHandler) {
    item.onMouseLeaveHandler();
  }
};
</script>
<style lang="scss" scoped>
$value-price-bg: #f7f0e6;

@keyframes product-images-cta-container-fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
.product-images-cta-container {
  position: absolute;
  top: rem-calc(16);
  left: rem-calc(16);
  opacity: 0;
  z-index: 10;

  animation-name: product-images-cta-container-fade;
  animation-delay: 0.1s;
  animation-fill-mode: forwards;
  animation-duration: 0.5s;

  .cta-item {
    align-items: center;
    background-color: $black;
    border: none;
    border-radius: 50%;
    color: $white;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    font-size: rem-calc(9);
    font-weight: bold;
    height: rem-calc(70);
    justify-content: center;
    letter-spacing: 0.05em;
    line-height: rem-calc(12);
    padding: 0;
    text-align: center;
    text-decoration: none;
    text-transform: uppercase;
    width: rem-calc(70);

    &:not(:last-child) {
      margin: rem-calc(0 0 16);
    }

    &.font-small {
      font-size: 8px;
      line-height: rem-calc(12);
    }
    &.font-big {
      font-size: 10px;
      line-height: rem-calc(14);
      padding: 0 rem-calc(12);
    }

    &.with-arrow-icon {
      padding: rem-calc(12) rem-calc(10) 0;
    }

    &--arrow-icon {
      margin-top: rem-calc(-3);
    }
  }
  .empty-image {
    height: 0;
    width: 0;
    border: none;
    outline: none;
  }

  .value-price-cta {
    cursor: auto;
    background-color: $value-price-bg;
    color: $black;

    :deep() {
      .price {
        display: inline-block;
        color: $black;
        line-height: 0.8;
        margin-bottom: rem-calc(4);
        font-weight: 700;
        sup {
          font-size: rem-calc(9);
          top: 2px;
        }
      }
      .value-price {
        display: inline-block;
        line-height: 1;
        font-size: rem-calc(8);
        font-weight: 700;
        text-transform: uppercase;
        color: $black;
      }
    }
  }
}
</style>
